import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShoppingBag, ArrowRight, Check, Loader2, ImageOff } from "lucide-react";
import { Product } from "@/data/products";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { toast } from "@/hooks/use-toast";
import { optimizeImageUrl } from "@/utils/imageTransform";
import Picture from "@/components/Picture";
import { productPath } from "@/utils/shareLinks";
import { isOfferActive, calcDiscountedPrice, getOfferBadge } from "@/utils/offerUtils";
import ProductBadge from "@/components/ProductBadge";
import StockIndicator from "@/components/StockIndicator";
import FavoriteButton from "@/components/FavoriteButton";
import { useFavorites } from "@/hooks/useFavorites";
import { flyToCart, announceCartUpdate, burstFromElement } from "@/utils/flyToCart";
import { variantDisplayLabel } from "@/utils/variantLabel";


interface QuickViewModalProps {
 product: Product;
 open: boolean;
 onOpenChange: (open: boolean) => void;
 /** Sub-variant id to seed the modal with (overrides stored selection). */
 initialSubId?: string | null;
 /** Fires whenever the sub-variant selection changes inside the modal. */
 onSubVariantChange?: (id: string | null) => void;
}

const QuickViewModal = ({
 product,
 open,
 onOpenChange,
 initialSubId,
 onSubVariantChange,
}: QuickViewModalProps) => {
 const { isAr } = useLanguage();
 const { addItem } = useCart();
 const { isFavorite, toggleFavorite } = useFavorites();
 const navigate = useNavigate();

 const name = isAr ? product.nameAr : product.name;
 const desc = isAr ? product.descriptionAr : product.description;
 const shortDesc = isAr ? product.shortDescriptionAr : product.shortDescription;
 const badge = isAr ? product.badgeAr : product.badge;

 // Persist per-product size/sub-variant picks across Quick View openings so
 // returning to the same product restores the last combo the user chose.
 const storageKey = `nb:quickview:${product.id}`;
 const readStored = (): { variant?: number; subId?: string | null } => {
 if (typeof window === "undefined") return {};
 try {
 const raw = window.localStorage.getItem(storageKey);
 return raw ? JSON.parse(raw) : {};
 } catch {
 return {};
 }
 };

 const [selectedVariant, setSelectedVariant] = useState(() => {
 const stored = readStored().variant;
 const max = (product.sizeVariants?.length ?? 1) - 1;
 return typeof stored === "number" && stored >= 0 && stored <= max ? stored : 0;
 });
 const [imgIndex, setImgIndex] = useState(0);
 // Track which hero URLs have fully decoded so we can keep the skeleton up
 // through a thumbnail swap and avoid the pre-decode flicker.
 const [loadedHeroes, setLoadedHeroes] = useState<Record<string, true>>({});
 // Track hero + thumbnail URLs that failed to load so we can swap in a
 // branded fallback instead of the browser's broken-image glyph.
 const [erroredImages, setErroredImages] = useState<Record<string, true>>({});
 const markErrored = (url: string) =>
 setErroredImages((prev) => (prev[url] ? prev : { ...prev, [url]: true }));

 const thumbRefs = useRef<Array<HTMLButtonElement | null>>([]);
 const sizeRefs = useRef<Array<HTMLButtonElement | null>>([]);

 // Roving arrow-key navigation. RTL swaps the horizontal arrows so the
 // "next" key always advances in visual reading direction.
 const rovingKeyHandler = (
 e: React.KeyboardEvent<HTMLButtonElement>,
 index: number,
 length: number,
 refs: React.MutableRefObject<Array<HTMLButtonElement | null>>,
 select: (i: number) => void,
 ) => {
 const nextKey = isAr ? "ArrowLeft" : "ArrowRight";
 const prevKey = isAr ? "ArrowRight" : "ArrowLeft";
 let target = -1;
 if (e.key === nextKey || e.key === "ArrowDown") target = (index + 1) % length;
 else if (e.key === prevKey || e.key === "ArrowUp") target = (index - 1 + length) % length;
 else if (e.key === "Home") target = 0;
 else if (e.key === "End") target = length - 1;
 if (target < 0) return;
 e.preventDefault();
 select(target);
 refs.current[target]?.focus();
 };
 const [selectedSubId, setSelectedSubId] = useState<string | null>(
 () => initialSubId ?? readStored().subId ?? null,
 );
 // Re-sync when the modal opens with an externally-controlled selection
 // (e.g. the parent ProductCard just committed a swatch pick).
 useEffect(() => {
 if (!open) return;
 if (initialSubId === undefined) return;
 setSelectedSubId((prev) => (prev === initialSubId ? prev : initialSubId));
 }, [open, initialSubId]);
 // Notify the parent whenever our selection changes so the card can mirror it.
 useEffect(() => {
 onSubVariantChange?.(selectedSubId);
 }, [selectedSubId, onSubVariantChange]);
 const [previewSubId, setPreviewSubId] = useState<string | null>(null);
 const subRefs = useRef<Array<HTMLButtonElement | null>>([]);

 const currentVariant = product.sizeVariants?.[selectedVariant];
 const subVariants = currentVariant?.subVariants ?? [];
 const hasSubVariants = subVariants.length > 0;
 const selectedSub = hasSubVariants
 ? subVariants.find((s) => s.id === selectedSubId) ?? null
 : null;

 // Keep the sub-variant selection valid for the active size. If nothing is
 // picked yet (or the stored id no longer exists), auto-select the first
 // in-stock option so Add to Cart is immediately actionable from the swatches.
 useEffect(() => {
 if (!hasSubVariants) {
 if (selectedSubId !== null) setSelectedSubId(null);
 return;
 }
 const stillValid = subVariants.some((s) => s.id === selectedSubId);
 if (stillValid) return;
 const firstAvailable =
 subVariants.find(
 (s) => s.stockStatus !== "out_of_stock" && s.stockQuantity !== 0,
 ) ?? subVariants[0];
 setSelectedSubId(firstAvailable?.id ?? null);
 }, [selectedVariant, hasSubVariants, subVariants, selectedSubId]);

 // Persist the current combo whenever it changes. Writes are cheap and
 // localStorage handles rapid toggles fine.
 useEffect(() => {
 if (typeof window === "undefined") return;
 try {
 window.localStorage.setItem(
 storageKey,
 JSON.stringify({ variant: selectedVariant, subId: selectedSubId }),
 );
 } catch {
 // Quota or private-mode failures are non-fatal; the modal still works.
 }
 }, [storageKey, selectedVariant, selectedSubId]);

 const previewSub = hasSubVariants
 ? subVariants.find((s) => s.id === previewSubId) ?? null
 : null;
 const mainSrc =
 (previewSub?.imageUrl) ||
 (selectedSub?.imageUrl) ||
 (currentVariant?.imageIsCustom && currentVariant.image ? currentVariant.image : product.image);

 const gallery = useMemo(
 () =>
 [
 { src: mainSrc, key: "main", label: isAr ? "المنتج" : "Main" },
 product.packagingImage && { src: product.packagingImage, key: "packaging", label: isAr ? "العبوة" : "Packaging" },
 product.lifestyleImage && { src: product.lifestyleImage, key: "lifestyle", label: isAr ? "أسلوب الحياة" : "Lifestyle" },
 product.ingredientsImage && { src: product.ingredientsImage, key: "ingredients", label: isAr ? "المكونات" : "Ingredients" },
 ].filter(Boolean) as { src: string; key: string; label: string }[],
 [mainSrc, product.packagingImage, product.lifestyleImage, product.ingredientsImage, isAr],
 );

 const safeIdx = Math.min(imgIndex, gallery.length - 1);
 const displayImage = gallery[safeIdx]?.src ?? mainSrc;

 // Warm the browser cache for an image URL without inserting it into the DOM.
 // Using a plain Image() request means the same optimized asset will be
 // served instantly when the user switches thumbnails or opens the lightbox.
 const prefetchCache = useRef<Set<string>>(new Set());
 const warm = (url: string) => {
 if (!url || prefetchCache.current.has(url)) return;
 prefetchCache.current.add(url);
 const img = new Image();
 img.decoding = "async";
 img.loading = "eager";
 img.src = url;
 };

 // On open, opportunistically prefetch the neighboring gallery images at the
 // hero size, keeps thumbnail clicks feeling instant. Runs on idle to avoid
 // competing with the active image's download.
 useEffect(() => {
 if (!open || gallery.length < 2) return;
 const cancel = { done: false };
 const schedule = (window as unknown as { requestIdleCallback?: (cb: IdleRequestCallback, opts?: IdleRequestOptions) => number })
 .requestIdleCallback ?? ((cb: (deadline: { timeRemaining: () => number }) => void) => window.setTimeout(() => cb({ timeRemaining: () => 50 }), 400));
 const handle = schedule(() => {
 if (cancel.done) return;
 const neighbors = [safeIdx + 1, safeIdx - 1, safeIdx + 2, safeIdx - 2]
 .map((i) => gallery[(i + gallery.length) % gallery.length])
 .filter((g, i, arr) => g && arr.findIndex((x) => x?.key === g.key) === i);
 neighbors.forEach((g) => warm(optimizeImageUrl(g!.src, { width: 900, quality: 85 })));
 });
 return () => {
 cancel.done = true;
 (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback?.(handle as number);
 };
 }, [open, safeIdx, gallery]);

 // Warm the hero-sized image for any sub-variant the user hovers/focuses so
 // committing the swatch swap feels instant. The active variant's image is
 // already loaded eagerly by <Picture>; siblings stay lazy until intent.
 const warmSubHero = (url?: string | null) => {
 if (!url) return;
 warm(optimizeImageUrl(url, { width: 900, quality: 85 }));
 };


 const subDelta = selectedSub?.priceDelta ?? 0;
 const basePrice = (currentVariant?.price ?? product.price) + subDelta;
 const activeOffer = isOfferActive(currentVariant?.offer) ? currentVariant?.offer : isOfferActive(product.offer) ? product.offer : null;
 const hasOffer = !!activeOffer;
 const discounted = hasOffer ? calcDiscountedPrice(basePrice, activeOffer!) : basePrice;

 const subOut =
 !!selectedSub &&
 (selectedSub.stockStatus === "out_of_stock" || selectedSub.stockQuantity === 0);
 const isOut = product.stockStatus === "out_of_stock" || product.stockQuantity === 0 || subOut;
 const needsSubChoice = hasSubVariants && !selectedSubId;

 const handleAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
 if (isOut) return;
 if (needsSubChoice) {
 toast({
 title: isAr ? "اختر الخيار" : "Choose an option",
 description: isAr ? "من فضلك اختر اللون أو الرائحة" : "Please pick a color or scent first",
 });
 // Focus the first sub-variant option to guide the user.
 subRefs.current[0]?.focus();
 return;
 }
 burstFromElement(e.currentTarget);
 try {
 addItem(
 product.id,
 product.sizeVariants ? selectedVariant : null,
 selectedSubId ?? null,
 );
 const img = document.querySelector<HTMLImageElement>("[data-quick-view-image] img");
 const message = isAr ? `تمت إضافة ${name} إلى السلة` : `${name} added to cart`;
 const scheduled = flyToCart(img, displayImage, { announce: message });
 if (!scheduled) announceCartUpdate(message);
 toast({ title: isAr ? "تمت الإضافة" : "Added to cart", description: name });
 } catch {
 toast({ title: isAr ? "تعذر الإضافة" : "Could not add", variant: "destructive" });
 }
 };

 return (
 <Dialog open={open} onOpenChange={onOpenChange}>
 <DialogContent className="max-w-5xl w-[95vw] max-h-[92vh] overflow-y-auto p-0 gap-0">
 <DialogTitle className="sr-only">{name}</DialogTitle>
 <DialogDescription className="sr-only">{shortDesc || desc?.slice(0, 120)}</DialogDescription>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
 {/* Image side */}
 <div className="relative bg-muted p-6 md:p-8" data-quick-view-image>
 <div className="relative aspect-square overflow-hidden rounded-sm">
 <div
 aria-hidden
 className="absolute inset-0 bg-center bg-cover scale-110 blur-2xl opacity-60"
 style={{ backgroundImage: `url(${optimizeImageUrl(displayImage, { width: 80, quality: 40 })})` }}
 />
 <div aria-hidden className="absolute inset-0 bg-background/25" />
 {/* Skeleton stays visible until the new hero fully decodes */}
 {!loadedHeroes[displayImage] && !erroredImages[displayImage] && (
 <div
 aria-hidden
 className="absolute inset-0 flex items-center justify-center"
 >
 <div className="absolute inset-6 rounded-sm bg-foreground/5 border border-foreground/10 animate-pulse" />
 <Loader2 size={28} className="relative text-foreground/50 animate-spin" />
 </div>
 )}
 {erroredImages[displayImage] ? (
 <div
 role="img"
 aria-label={isAr ? "تعذر تحميل الصورة" : "Image unavailable"}
 className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted text-muted-foreground/70 px-4 text-center"
 >
 <ImageOff size={32} strokeWidth={1.5} aria-hidden="true" />
 <span className="font-body text-[10px] uppercase tracking-[0.2em]">
 {isAr ? "الصورة غير متاحة" : "Image unavailable"}
 </span>
 </div>
 ) : (
 <Picture
 key={displayImage}
 src={displayImage}
 widths={[500, 700, 900, 1200]}
 displayWidth={900}
 quality={85}
 sizes="(min-width: 768px) 45vw, 92vw"
 width={900}
 height={900}
 alt={`${name} by Nil Beauty, ${gallery[safeIdx]?.label ?? "product"}`}
 className={`relative w-full h-full object-contain p-3 transition-opacity duration-200 ${
 loadedHeroes[displayImage] ? "opacity-100" : "opacity-0"
 }`}
 loading="eager"
 fetchPriority="high"
 decoding="async"
 onLoad={(e) => {
 const img = e.currentTarget;
 const url = displayImage;
 const reveal = () =>
 setLoadedHeroes((prev) => (prev[url] ? prev : { ...prev, [url]: true }));
 if (typeof img.decode === "function") {
 img.decode().then(reveal).catch(reveal);
 } else {
 reveal();
 }
 }}
 onError={() => markErrored(displayImage)}
 />
 )}
 <div className={`absolute top-3 ${isAr ? "left-3" : "right-3"}`}>
 <FavoriteButton
 isFavorite={isFavorite(product.id)}
 onToggle={() => toggleFavorite(product.id, { productName: product.name })}
 size={18}
 />
 </div>
 <div className={`absolute bottom-3 ${isAr ? "right-3" : "left-3"}`}>
 <StockIndicator stockQuantity={product.stockQuantity} stockStatus={product.stockStatus} />
 </div>
 </div>

 {gallery.length > 1 && (
 <div
 role="radiogroup"
 aria-label={isAr ? "معرض صور المنتج" : "Product image gallery"}
 className="flex gap-2 mt-4 flex-wrap"
 >
 {gallery.map((img, i) => {
 const active = i === safeIdx;
 const warmHero = () => warm(optimizeImageUrl(img.src, { width: 900, quality: 85 }));
 return (
 <button
 key={img.key}
 type="button"
 ref={(el) => (thumbRefs.current[i] = el)}
 onClick={() => setImgIndex(i)}
 onMouseEnter={warmHero}
 onFocus={warmHero}
 onTouchStart={warmHero}
 onKeyDown={(e) => rovingKeyHandler(e, i, gallery.length, thumbRefs, setImgIndex)}
 role="radio"
 aria-checked={active}
 aria-label={img.label}
 tabIndex={active ? 0 : -1}
 className={`w-14 h-14 rounded-sm overflow-hidden border-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
 active ? "border-primary shadow-md" : "border-border opacity-60 hover:opacity-100"
 }`}
 >
 {erroredImages[img.src] ? (
 <span
 role="img"
 aria-label={img.label}
 className="flex w-full h-full items-center justify-center bg-muted text-muted-foreground/70"
 >
 <ImageOff size={16} strokeWidth={1.5} aria-hidden="true" />
 </span>
 ) : (
 <Picture
 src={img.src}
 widths={[112, 224]}
 displayWidth={112}
 quality={70}
 sizes="56px"
 width={56}
 height={56}
 alt=""
 className="w-full h-full object-contain bg-background p-1"
 loading={active ? "eager" : "lazy"}
 fetchPriority={active ? "high" : "low"}
 decoding="async"
 withSkeleton
 wrapperClassName="w-full h-full"
 onError={() => markErrored(img.src)}
 />
 )}
 </button>
 );
 })}
 </div>
 )}
 </div>

 {/* Details side */}
 <div className="p-6 md:p-8 flex flex-col">
 {badge && <ProductBadge className="text-[11px] px-3 py-1 mb-3 w-fit">{badge}</ProductBadge>}
 <h2 className="font-serif text-2xl md:text-3xl font-bold leading-tight mb-2">{name}</h2>
 {shortDesc && <p className="font-body text-sm text-muted-foreground mb-4">{shortDesc}</p>}

 <div className="flex items-baseline gap-3 mb-5">
 {hasOffer ? (
 <>
 <span className="font-serif text-2xl font-bold text-destructive">
 {discounted.toFixed(0)} {isAr ? "ج.م" : "EGP"}
 </span>
 <span className="font-serif text-base text-muted-foreground line-through">
 {basePrice.toFixed(0)} {isAr ? "ج.م" : "EGP"}
 </span>
 <ProductBadge className="text-[10px] px-2 py-0.5">{getOfferBadge(activeOffer, isAr)}</ProductBadge>
 </>
 ) : (
 <span className="font-serif text-2xl font-bold text-primary">
 {basePrice.toFixed(0)} {isAr ? "ج.م" : "EGP"}
 </span>
 )}
 </div>

 {product.sizeVariants && product.sizeVariants.length > 0 && (
 <div className="mb-5">
 <p id="qv-size-label" className="font-body text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
 {isAr ? "الحجم" : "Size"}
 </p>
 <div
 role="radiogroup"
 aria-labelledby="qv-size-label"
 className="flex flex-wrap gap-2"
 >
 {product.sizeVariants.map((v, i) => {
 const lbl = variantDisplayLabel(v, isAr, product) || v.size;
 const active = i === selectedVariant;
 return (
 <button
 key={v.id || v.size}
 type="button"
 ref={(el) => (sizeRefs.current[i] = el)}
 onClick={() => setSelectedVariant(i)}
 onKeyDown={(e) =>
 rovingKeyHandler(e, i, product.sizeVariants!.length, sizeRefs, setSelectedVariant)
 }
 role="radio"
 aria-checked={active}
 aria-label={lbl}
 tabIndex={active ? 0 : -1}
 className={`px-4 py-2 border rounded-sm font-body text-sm transition-all inline-flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
 active
 ? "border-primary bg-primary/10 text-primary"
 : "border-border hover:border-primary/60"
 }`}
 >
 {active && <Check size={13} aria-hidden="true" />}
 {lbl}
 </button>
 );
 })}
 </div>
 </div>
 )}

 {hasSubVariants && (
 <div className="mb-5">
 <p id="qv-sub-label" className="font-body text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
 {isAr ? "الخيار" : "Option"}
 {(previewSub || selectedSub) && (
 <span className="ms-2 text-foreground normal-case tracking-normal">
 {(() => {
 const s = previewSub || selectedSub!;
 return isAr
 ? s.poeticNameAr || s.scentAr || s.poeticName || s.scent || ""
 : s.poeticName || s.scent || "";
 })()}
 </span>
 )}
 </p>
 <div
 role="radiogroup"
 aria-labelledby="qv-sub-label"
 className="flex items-center flex-wrap gap-2"
 onMouseLeave={() => setPreviewSubId(null)}
 >
 {subVariants.map((sv, i) => {
 const id = sv.id ?? String(i);
 const active = id === selectedSubId;
 const label =
 (isAr
 ? sv.poeticNameAr || sv.scentAr || sv.poeticName || sv.scent
 : sv.poeticName || sv.scent) ||
 (isAr ? "خيار" : "Option");
 const soldOut = sv.stockStatus === "out_of_stock" || sv.stockQuantity === 0;
 const common = {
 ref: (el: HTMLButtonElement | null) => (subRefs.current[i] = el),
 type: "button" as const,
 role: "radio" as const,
 "aria-checked": active,
 "aria-label": label,
 "aria-disabled": soldOut,
 title: label,
 tabIndex: active || (!selectedSubId && i === 0) ? 0 : -1,
 onMouseEnter: () => {
 setPreviewSubId(id);
 warmSubHero(sv.imageUrl);
 },
 onFocus: () => {
 setPreviewSubId(id);
 warmSubHero(sv.imageUrl);
 },
 onTouchStart: () => warmSubHero(sv.imageUrl),
 onClick: () => !soldOut && setSelectedSubId(id),
 onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) =>
 rovingKeyHandler(e, i, subVariants.length, subRefs, (n) => {
 const target = subVariants[n];
 const targetId = target.id ?? String(n);
 if (!(target.stockStatus === "out_of_stock" || target.stockQuantity === 0)) {
 setSelectedSubId(targetId);
 }
 }),
 };
 if (sv.colorHex) {
 return (
 <button
 {...common}
 key={id}
 className={`w-7 h-7 rounded-full border transition-transform duration-200 hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
 active ? "border-primary ring-2 ring-primary/40" : "border-border/70"
 } ${soldOut ? "opacity-40 line-through cursor-not-allowed" : ""}`}
 style={{ backgroundColor: sv.colorHex }}
 />
 );
 }
 return (
 <button
 {...common}
 key={id}
 className={`px-3 py-1 rounded-full border text-[11px] uppercase tracking-[0.16em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
 active
 ? "border-primary bg-primary/10 text-primary"
 : "border-border/70 text-muted-foreground hover:border-primary hover:text-primary"
 } ${soldOut ? "opacity-40 line-through cursor-not-allowed" : ""}`}
 >
 {label}
 </button>
 );
 })}
 </div>
 </div>
 )}



 {desc && (
 <p className="font-body text-sm text-muted-foreground leading-relaxed mb-6 line-clamp-4">
 {desc}
 </p>
 )}

 <div className="mt-auto space-y-3">
 <button
 type="button"
 onClick={handleAdd}
 disabled={isOut}
 aria-disabled={isOut || needsSubChoice}
 className="w-full inline-flex items-center justify-center gap-2 bg-foreground text-background hover:bg-primary hover:text-primary-foreground transition-colors font-body text-sm uppercase tracking-widest py-3 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed"
 >
 <ShoppingBag size={16} aria-hidden="true" />
 {isOut
 ? isAr ? "غير متوفر" : "Sold out"
 : needsSubChoice
 ? isAr ? "اختر الخيار" : "Choose an option"
 : isAr ? "أضف إلى السلة" : "Add to Cart"}
 </button>
 <Link
 to={productPath(product.id)}
 onClick={() => onOpenChange(false)}
 className="w-full inline-flex items-center justify-center gap-2 border border-border hover:border-primary hover:text-primary transition-colors font-body text-sm uppercase tracking-widest py-3 rounded-sm"
 >
 {isAr ? "عرض التفاصيل الكاملة" : "View full details"}
 <ArrowRight size={14} className={isAr ? "rotate-180" : ""} aria-hidden="true" />
 </Link>
 </div>
 </div>
 </div>
 </DialogContent>
 </Dialog>
 );
};

export default QuickViewModal;
